import { useEffect, useMemo, useState } from 'react';
import { Save, Info, MessageSquare, CalendarClock } from 'lucide-react';
import { routingAPI } from '../services/api.js';

// The three NEW inbound call-handling actions that sit alongside the existing
// voicemail + forwarding (RoutingEditor). All three are stored in the SAME
// /routing config (additive JSON keys), so this editor GETs the whole config,
// edits only its three sections, and PUTs them back (the merge on the server is
// per-section, so voicemail/forwarding edited in RoutingEditor are untouched).
//
//   • Provide information — the AI answers from Business Info + FAQ + Knowledge.
//   • Take a message      — capture name/number/message (emailed if set).
//   • Schedule            — offer a booking link / calendar (v1).

const DEFAULTS = {
  provideInformation: { enabled: true },
  takeMessage: {
    enabled: false,
    emailTo: '',
    prompt: 'Can I take a message? May I get your name, the best number to reach you, and what this is about?'
  },
  scheduling: {
    enabled: false,
    bookingUrl: '',
    calendarName: '',
    sayWhenOffering: 'I can help you book a time — would you like me to send you our scheduling link?'
  }
};

function pick(cfg) {
  return {
    provideInformation: { ...DEFAULTS.provideInformation, ...(cfg?.provideInformation || {}) },
    takeMessage: {
      ...DEFAULTS.takeMessage,
      ...(cfg?.takeMessage || {}),
      emailTo: cfg?.takeMessage?.emailTo || ''
    },
    scheduling: {
      ...DEFAULTS.scheduling,
      ...(cfg?.scheduling || {}),
      bookingUrl: cfg?.scheduling?.bookingUrl || '',
      calendarName: cfg?.scheduling?.calendarName || ''
    }
  };
}

export default function CallActionsEditor() {
  const [state, setState] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await routingAPI.get();
        const next = pick(r?.data || {});
        if (!cancelled) { setState(next); setOriginal(next); }
      } catch (err) {
        if (!cancelled && err.status !== 404) setError(err.message || 'Failed to load call actions.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(() => JSON.stringify(state) !== JSON.stringify(original), [state, original]);

  const set = (section, patch) => setState((s) => ({ ...s, [section]: { ...s[section], ...patch } }));

  const onSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        provideInformation: { enabled: state.provideInformation.enabled },
        takeMessage: {
          enabled: state.takeMessage.enabled,
          emailTo: state.takeMessage.emailTo.trim() || null,
          prompt: state.takeMessage.prompt
        },
        scheduling: {
          enabled: state.scheduling.enabled,
          bookingUrl: state.scheduling.bookingUrl.trim() || null,
          calendarName: state.scheduling.calendarName.trim() || null,
          sayWhenOffering: state.scheduling.sayWhenOffering
        }
      };
      const r = await routingAPI.update(payload);
      const next = pick(r?.data || {});
      setState(next);
      setOriginal(next);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card">
      <h2 className="text-xl text-stone-100 mb-1">Call actions</h2>
      <p className="text-sm text-stone-400 mb-6">
        What the AI can do for an inbound caller. Turn each action on and configure
        it. Voicemail and forwarding are configured below.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      <div className="space-y-6">
        {/* Provide information */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Info size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Provide information to the caller</h3>
            </div>
            <Toggle checked={state.provideInformation.enabled} onChange={(v) => set('provideInformation', { enabled: v })} disabled={loading || saving} />
          </header>
          <p className="text-xs text-stone-500">
            The AI answers the caller's questions using your Business Info, FAQs, and
            Knowledge (managed below in “Answer material”). Turn this off to make the
            AI a pure message-taker / router that doesn't answer questions.
          </p>
        </div>

        {/* Take a message */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Take a message</h3>
            </div>
            <Toggle checked={state.takeMessage.enabled} onChange={(v) => set('takeMessage', { enabled: v })} disabled={loading || saving} />
          </header>
          <div className="space-y-4">
            <div>
              <label className="field-label">What the AI says to collect the message</label>
              <textarea
                className="field-input min-h-[70px]"
                value={state.takeMessage.prompt}
                onChange={(e) => set('takeMessage', { prompt: e.target.value })}
                disabled={loading || saving || !state.takeMessage.enabled}
                maxLength={500}
              />
            </div>
            <div>
              <label className="field-label">Email messages to</label>
              <input
                type="email"
                className="field-input"
                placeholder="ops@yourcompany.com"
                value={state.takeMessage.emailTo}
                onChange={(e) => set('takeMessage', { emailTo: e.target.value })}
                disabled={loading || saving || !state.takeMessage.enabled}
              />
            </div>
          </div>
        </div>

        {/* Schedule an appointment */}
        <div className="rounded-xl border border-stone-800 p-5">
          <header className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarClock size={18} className="text-alfred-gold" />
              <h3 className="text-stone-100 font-medium">Schedule an appointment</h3>
            </div>
            <Toggle checked={state.scheduling.enabled} onChange={(v) => set('scheduling', { enabled: v })} disabled={loading || saving} />
          </header>
          <p className="text-xs text-stone-500 mb-4">
            v1: the AI offers to book and points the caller at your scheduling link.
            Live calendar availability is a planned next step.
          </p>
          <div className="space-y-4">
            <div>
              <label className="field-label">Booking link</label>
              <input
                type="url"
                className="field-input"
                placeholder="https://calendly.com/your-team/intro"
                value={state.scheduling.bookingUrl}
                onChange={(e) => set('scheduling', { bookingUrl: e.target.value })}
                disabled={loading || saving || !state.scheduling.enabled}
              />
            </div>
            <div>
              <label className="field-label">Calendar / service name (optional)</label>
              <input
                className="field-input"
                placeholder="e.g. New patient consult"
                value={state.scheduling.calendarName}
                onChange={(e) => set('scheduling', { calendarName: e.target.value })}
                disabled={loading || saving || !state.scheduling.enabled}
              />
            </div>
            <div>
              <label className="field-label">What the AI says when offering to book</label>
              <input
                className="field-input"
                value={state.scheduling.sayWhenOffering}
                onChange={(e) => set('scheduling', { sayWhenOffering: e.target.value })}
                disabled={loading || saving || !state.scheduling.enabled}
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" className="btn-primary" onClick={onSave} disabled={!dirty || saving || loading}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save call actions'}
          </button>
          {savedAt && !dirty && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
        </div>
      </div>
    </section>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
        disabled={disabled}
      />
      <span className="text-stone-300">{checked ? 'On' : 'Off'}</span>
    </label>
  );
}
